import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { DashboardDealInvitesEnumType, FinancialInstrumentEnumType } from '__generated__/graphql';
import { FC } from 'react';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';

import { formatCurrencyToShort } from 'utils/format-helper';

const DASHBOARD_INVITES_QUERY = gql(`
  query GetActiveInvitesOfUser($cursor: ID, $filters: DashboardDealInvitesFilterType) {
    dashboard {
      id
      dealInvites(cursor: $cursor, filters: $filters) {
        nodes {
          id
          deal {
            id
            closingDate
            companyInformation {
              id
              company {
                id
                image
                name
              }
            }
            dealTerms {
              id
              transactionType
              valuationOrCap
              fundingRoundName
              financialInstrument
              floorPrice
            }
            syndicate {
              id
              name
            }
          }
          status
          createdAt
        }
        pageInfo {
          hasNextPage
          cursor
        }
      }
    }
  }
`);

const ActiveInvitesOfUser: FC = () => {
  const { loading, error, data, refetch, fetchMore } = useQuery(DASHBOARD_INVITES_QUERY, {
    variables: {
      filters: {
        type: DashboardDealInvitesEnumType.Invited,
      },
    },
  });

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const invites = data.dashboard.dealInvites.nodes;
  const pageInfo = data.dashboard.dealInvites.pageInfo;

  return (
    <DataTable
      data={invites}
      onClick={invite => window.open(`/invest/deal-invites/${invite.id}`, '_blank')}
      emptyListTitle="No new deal invites"
      emptyListDescription="Please check back later for new deal invites."
      columns={[
        {
          label: 'Company',
          fieldName: 'deal.companyInformation.company',
          type: 'IMAGE_WITH_NAME',
        },
        {
          label: 'Syndicate',
          fieldName: 'deal.syndicate.name',
        },
        // {
        //   label: 'Round',
        //   fieldName: 'deal.dealTerms.fundingRoundName',
        //   type: 'FUNDING_ROUND',
        // },
        {
          label: 'Valuation/Floor',
          fieldName: 'deal.dealTerms',
          type: 'CUSTOM',
          customValue: (dealTerms: any) => {
            return dealTerms.financialInstrument === FinancialInstrumentEnumType.Equity ||
              dealTerms.financialInstrument === FinancialInstrumentEnumType.Ccps
              ? formatCurrencyToShort(dealTerms.valuationOrCap)
              : dealTerms.floorPrice
              ? formatCurrencyToShort(dealTerms.floorPrice)
              : formatCurrencyToShort(dealTerms.valuationOrCap);
          },
        },
        {
          label: 'Invitation Date',
          fieldName: 'createdAt',
          type: 'DATE',
        },
        {
          label: 'Deadline',
          fieldName: 'deal.closingDate',
          type: 'DATE',
        },
      ]}
      hasNextPage={pageInfo.hasNextPage}
      onLoadMore={() =>
        fetchMore({
          variables: {
            cursor: pageInfo.cursor,
          },
        })
      }
    />
  );
};

export default ActiveInvitesOfUser;
